import React from 'react'
import { Search, FileText, HelpCircle, Zap } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'

const SUGGESTIONS = [
  { icon: Search, label: 'Find leads matching my ICP', sub: 'Prospect by industry, size, or signal' },
  { icon: FileText, label: 'Draft an outreach email', sub: 'Personalised cold emails in seconds' },
  { icon: Zap, label: 'Show my campaign performance', sub: 'Open rates, replies, and pipeline' },
  { icon: HelpCircle, label: 'Research a prospect or company', sub: 'Signals, news, and call prep briefs' },
]

const WELCOMES = [
  { headline: (name?: string) => `Back at it${name ? `, ${name}` : ''}`, sub: 'Ready to fill the pipeline?' },
  { headline: (name?: string) => `Let's find your next deal${name ? `, ${name}` : ''}`, sub: 'Who are we targeting today?' },
  { headline: (name?: string) => `Good to see you${name ? `, ${name}` : ''}`, sub: 'Your pipeline won\'t build itself.' },
  { headline: (name?: string) => `Ready to prospect${name ? `, ${name}` : ''}?`, sub: 'Let\'s find leads that actually convert.' },
  { headline: (name?: string) => `Time to book some meetings${name ? `, ${name}` : ''}`, sub: 'What are we closing today?' },
]

interface WelcomeScreenProps {
  userName?: string
  onSuggestionClick: (text: string) => void
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ userName, onSuggestionClick }) => {
  const { currentTheme, isDarkMode } = useTheme()
  const picked = React.useMemo(() => WELCOMES[Math.floor(Math.random() * WELCOMES.length)] ?? WELCOMES[0]!, [])

  return (
    <div className="flex flex-col items-center justify-center flex-1 px-6 py-16 min-h-full">
      <img
        src={isDarkMode ? '/Zeph-White-Logo.png' : '/Zeph-Black-Logo.png'}
        alt="Zeph logo"
        className="object-contain w-16 h-16 mb-6"
      />

      <h1 className="text-2xl font-semibold mb-2 text-center" style={{ color: currentTheme.text }}>
        {picked!.headline(userName)}
      </h1>
      <p className="text-sm mb-10 text-center" style={{ color: currentTheme.secondrytext }}>
        {picked!.sub}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-xl">
        {SUGGESTIONS.map(({ icon: Icon, label, sub }) => (
          <button
            key={label}
            onClick={() => onSuggestionClick(label)}
            className="flex items-start gap-3 px-4 py-3.5 rounded-2xl text-left transition-all duration-200"
            style={{
              backgroundColor: currentTheme.secondary,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: currentTheme.cardShadow,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = currentTheme.accent
              e.currentTarget.style.boxShadow = currentTheme.cardShadowHover
              e.currentTarget.style.transform = 'translateY(-2px)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = currentTheme.border
              e.currentTarget.style.boxShadow = currentTheme.cardShadow
              e.currentTarget.style.transform = 'translateY(0)'
            }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
              style={{ backgroundColor: `${currentTheme.accent}1A` }}
            >
              <Icon className="w-4 h-4" style={{ color: currentTheme.accent }} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium leading-snug" style={{ color: currentTheme.text }}>{label}</p>
              <p className="text-xs mt-0.5" style={{ color: currentTheme.mutedText }}>{sub}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
